import { useCallback, useEffect, useMemo, useState } from "react";
import { format, parseISO, addDays } from "date-fns";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { canAccessCentroDiurno } from "../../utils/centroDiurnoAccess";
import {
  getPrenotazioni,
  createPrenotazione,
  deletePrenotazione,
  getDisponibilita,
  setPacchetto,
  getPacchettiConfig,
} from "../../utils/centroDiurnoApi";
import PageHeader from "../../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import CdAgendaGrid, { agendaRangeFromView, agendaWeekLabel } from "../../components/centro-diurno/CdAgendaGrid";
import { resolveStatoOspite, STATO_ATTIVO } from "../../utils/ospiteStato";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CentroDiurnoAgenda() {
  const { token, user, hasPermission, activeStruttura } = useAuth();
  const [dataRef, setDataRef] = useState(format(new Date(), "yyyy-MM-dd"));
  const [prenotazioni, setPrenotazioni] = useState([]);
  const [agendaGiorni, setAgendaGiorni] = useState([]);
  const [ospiti, setOspiti] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nuovo, setNuovo] = useState({
    fascicolo_id: "",
    data: format(new Date(), "yyyy-MM-dd"),
    fascia: "intera",
    tipo: "singola",
    trasporto_ar: true,
    pacchetto_tipo: "mensile_assistito",
  });
  const [loading, setLoading] = useState(false);
  const [pacchettiCatalogo, setPacchettiCatalogo] = useState([]);

  const canManage = hasPermission("centro_diurno:manage");
  const weekCaption = useMemo(() => agendaWeekLabel(dataRef), [dataRef]);

  const pacchettiAttivi = useMemo(
    () => pacchettiCatalogo.filter((p) => p.attivo !== false),
    [pacchettiCatalogo]
  );

  const ospitiAttivi = useMemo(
    () =>
      ospiti
        .filter((o) => !o.archiviata && resolveStatoOspite(o) === STATO_ATTIVO)
        .sort((a, b) => {
          const cmpCognome = (a.cognome || "").localeCompare(b.cognome || "", "it", {
            sensitivity: "base",
          });
          if (cmpCognome !== 0) return cmpCognome;
          return (a.nome || "").localeCompare(b.nome || "", "it", { sensitivity: "base" });
        }),
    [ospiti]
  );

  const ospiteSelezionato = useMemo(
    () => ospitiAttivi.find((o) => o.id === nuovo.fascicolo_id),
    [ospitiAttivi, nuovo.fascicolo_id]
  );

  const pacchettoOspite = ospiteSelezionato?.amministrativa?.pacchetto_centro_diurno;

  const load = useCallback(async () => {
    if (!token) return;
    const [da, a] = agendaRangeFromView(dataRef, "settimana");
    try {
      const [pre, disp, fascRes, cfg] = await Promise.all([
        getPrenotazioni(token, { da, a }),
        getDisponibilita(token, da, a),
        axios.get(`${API_URL}/api/fascicoli-assistenziali`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        getPacchettiConfig(token),
      ]);
      setPrenotazioni(pre.prenotazioni || []);
      setAgendaGiorni(disp.giorni || []);
      setOspiti((fascRes.data?.fascicoli || fascRes.data || []).filter(Boolean));
      const catalogo = cfg.pacchetti || [];
      setPacchettiCatalogo(catalogo);
      const firstActive = catalogo.find((p) => p.attivo !== false);
      if (firstActive) {
        setNuovo((prev) => ({
          ...prev,
          pacchetto_tipo: prev.pacchetto_tipo || firstActive.id,
          trasporto_ar: prev.trasporto_ar ?? Boolean(firstActive.trasporto_ar_default),
        }));
      }
    } catch (e) {
      toast.error("Errore caricamento agenda");
    }
  }, [token, dataRef, activeStruttura?.id]);

  useEffect(() => {
    if (canAccessCentroDiurno(user)) load();
  }, [load, user]);

  const shiftWeek = (delta) => {
    setDataRef((prev) => format(addDays(parseISO(prev), delta * 7), "yyyy-MM-dd"));
  };

  const handleCreate = async () => {
    if (!nuovo.fascicolo_id) {
      toast.error("Seleziona un ospite");
      return;
    }
    setLoading(true);
    try {
      await createPrenotazione(token, nuovo);
      if (nuovo.pacchetto_tipo) {
        await setPacchetto(token, nuovo.fascicolo_id, {
          tipo: nuovo.pacchetto_tipo,
          trasporto_ar: nuovo.trasporto_ar,
        });
      }
      toast.success("Prenotazione creata");
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deletePrenotazione(token, id);
      setPrenotazioni((prev) => prev.filter((p) => p.id !== id));
      toast.success("Prenotazione annullata");
      load();
    } catch (e) {
      toast.error("Errore annullamento");
    }
  };

  if (!canAccessCentroDiurno(user)) {
    return <div className="p-8 text-center text-slate-600">Modulo non attivo</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="p-4 md:p-6 lg:p-8 max-w-6xl mx-auto space-y-6">
        <PageHeader
          title="Agenda e Prenotazioni"
          subtitle="Calendario prenotazioni con controllo saturazione (max 20)"
          icon={CalendarDays}
          iconContainerClassName="bg-gradient-to-br from-emerald-500 to-emerald-700"
          iconClassName="text-white"
        />

        <div className="flex flex-wrap gap-3 items-center">
          <Input type="date" value={dataRef} onChange={(e) => setDataRef(e.target.value)} className="w-auto bg-white" />
          {canManage && (
            <Button
              className="bg-[#4CAF50] hover:bg-[#43A047]"
              onClick={() => {
                setNuovo((prev) => ({ ...prev, data: dataRef }));
                setDialogOpen(true);
              }}
            >
              <Plus className="w-4 h-4 mr-1" /> Nuova prenotazione
            </Button>
          )}
        </div>

        <div className="min-w-0">
          <CdAgendaGrid
            giorni={agendaGiorni}
            capacita={20}
            view="settimana"
            showPeriodNav
            periodLabel={weekCaption}
            onPrevPeriod={() => shiftWeek(-1)}
            onNextPeriod={() => shiftWeek(1)}
            agendaLink={null}
          />
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Prenotazioni della settimana</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {prenotazioni.length === 0 ? (
                <p className="text-sm text-slate-500">Nessuna prenotazione</p>
              ) : (
                prenotazioni.map((p) => (
                  <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-100">
                    <div>
                      <p className="font-medium text-slate-800">{p.ospite_nome || p.fascicolo_id}</p>
                      <p className="text-xs text-slate-500">
                        {p.data} · {p.fascia} · {p.tipo}
                        {p.trasporto_ar ? " · trasporto A/R" : ""}
                      </p>
                    </div>
                    {canManage && p.stato !== "annullata" && (
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova prenotazione / nuovo accesso</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label>Ospite</Label>
                <Select value={nuovo.fascicolo_id} onValueChange={(v) => setNuovo({ ...nuovo, fascicolo_id: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona ospite" />
                  </SelectTrigger>
                  <SelectContent>
                    {ospitiAttivi.map((o) => (
                      <SelectItem key={o.id} value={o.id}>
                        {o.nome_completo ||
                          `${o.cognome || ""} ${o.nome || ""}`.trim() ||
                          o.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Pacchetto iniziale</Label>
                <Select
                  value={nuovo.pacchetto_tipo}
                  onValueChange={(v) => {
                    const pacc = pacchettiAttivi.find((p) => p.id === v);
                    setNuovo({
                      ...nuovo,
                      pacchetto_tipo: v,
                      trasporto_ar: pacc ? Boolean(pacc.trasporto_ar_default) : nuovo.trasporto_ar,
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {pacchettiAttivi.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.nome}
                        {p.is_carnet ? ` (${p.carnet_ingressi} ingressi)` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {pacchettoOspite?.is_carnet || pacchettoOspite?.tipo === "carnet_10" ? (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-900">
                  Carnet attuale:{" "}
                  <strong>{pacchettoOspite.carnet_residui ?? "—"}</strong> ingressi residui
                  {pacchettoOspite.carnet_scadenza ? ` · scade il ${pacchettoOspite.carnet_scadenza}` : ""}
                </div>
              ) : null}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Data</Label>
                  <Input type="date" value={nuovo.data} onChange={(e) => setNuovo({ ...nuovo, data: e.target.value })} />
                </div>
                <div>
                  <Label>Fascia</Label>
                  <Select value={nuovo.fascia} onValueChange={(v) => setNuovo({ ...nuovo, fascia: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mattina">Mattina 08:00-13:00</SelectItem>
                      <SelectItem value="pomeriggio">Pomeriggio 13:00-18:00</SelectItem>
                      <SelectItem value="intera">Giornata intera</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={nuovo.trasporto_ar}
                  onChange={(e) => setNuovo({ ...nuovo, trasporto_ar: e.target.checked })}
                />
                Servizio trasporto A/R attivo
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Annulla
              </Button>
              <Button className="bg-[#4CAF50] hover:bg-[#43A047]" onClick={handleCreate} disabled={loading}>
                Conferma prenotazione
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
